import type { Dayjs } from "dayjs";
import type { YAxisTitleOptions } from "highcharts";
import { renderToString } from "react-dom/server";
import styled from "styled-components";

import {
    combineConfigs,
    noLegendConfig,
    xAxisCrosshair,
    xAxisLabelsConfig,
    yAxisLabelsConfig,
} from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { ColumnDefinitionWithoutTitle } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { tooltipPositioner } from "app/services/HighchartsPositioner";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter, spendFilter } from "app/filters/ngFilters";
import { NA_VALUE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import type { Entries } from "pro-features/types";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getTooltipHeaderElement } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";

import type { BreakdownByGranularity } from "../../../advertisers/overview/components/types";
import { EDataType } from "../../../advertisers/overview/components/types";
import {
    MAX_DATA_POINTS_FOR_ROTATION,
    yAxisFormatter,
    xAxisFormatter,
    legendToFormatterMap,
    getTickPositionerConfig,
    getXAxisTickPositioner,
} from "../utils/graphUtils";

const TRANSLATION_KEYS = {
    SPEND: "ad.intelligence.overview.performance.over.time.widget.spend",
    IMPRESSIONS: "ad.intelligence.overview.performance.over.time.widget.impressions",
    VISITS: "ad.intelligence.overview.performance.over.time.widget.visits",
    PAGE_VIEWS: "ad.intelligence.overview.performance.over.time.widget.pageViews",
} as const;

const thirdMetricToTranslationKeyMap: Record<
    Extract<EDataType, EDataType.VISITS | EDataType.PAGE_VIEWS>,
    keyof typeof TRANSLATION_KEYS
> = {
    [EDataType.VISITS]: "VISITS",
    [EDataType.PAGE_VIEWS]: "PAGE_VIEWS",
};

const TooltipTitle = styled.span`
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

export const YAxisTitle = styled.span`
    text-transform: capitalize;
    ${setFontVariant("dataTable1", colorsPalettes.carbon[400])};
`;

interface ITooltipData {
    label: {
        value: string;
        color: string;
    };
    count: {
        value: string;
    };
    change: {
        value: string;
        isDecrease: boolean | undefined;
        unsigned: boolean;
    };
}

const tooltipColumnDefinition: ColumnDefinitionWithoutTitle<ITooltipData>[] = [
    {
        key: "0",
        selector: "label" as const,
        align: "left",
        size: "172px",
        render: ({ label }) => (
            <ChartTooltip.LabelCell
                label={label.value}
                backgroundColor={label.color}
            />
        ),
    },
    {
        key: "1",
        selector: "count" as const,
        align: "right",
        size: "50px",
        borderRight: true,
        render: ({ count }) => <ChartTooltip.ValueCell value={count.value} />,
    },
    {
        key: "2",
        selector: "change" as const,
        align: "right",
        size: "80px",
        render: ({ change }) => (
            <ChartTooltip.ChangeCell
                value={change.value}
                isDecrease={change.isDecrease}
                unsigned={change.unsigned}
            />
        ),
    },
];

const getYAxisTitleConfig = (
    hasNoData: boolean,
    text: string,
    extra: Partial<YAxisTitleOptions>,
): YAxisTitleOptions => ({
    text: hasNoData ? undefined : renderToString(<YAxisTitle>{translate(text)}</YAxisTitle>),
    align: "high",
    y: -36,
    rotation: 0,
    useHTML: true,
    ...extra,
});

export const getChartConfig = (
    granularity: GranularityNames,
    to: Dayjs,
    selectedLegends: EDataType[],
    hasNoData: boolean,
    breakdownData: Entries<BreakdownByGranularity>,
    isPreview: boolean = false,
    thirdMetric: EDataType,
    hideLegend: boolean = false,
) => {
    const isSinglePoint = breakdownData.length === 1;

    return combineConfigs(
        {
            yAxisFormatter,
            xAxisFormatter,
        },
        [
            noLegendConfig,
            yAxisLabelsConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            {
                chart: {
                    height: 208,
                    margin: [isPreview || hideLegend ? 24 : 48, 48, 24, 48],
                },
                yAxis: selectedLegends.map((legend, index) => ({
                    title: getYAxisTitleConfig(hasNoData || selectedLegends.length === 1, legend, {
                        textAlign: index === 0 ? "left" : "right",
                        x: index === 0 ? 16 : -16,
                    }),
                    labels: {
                        formatter: function () {
                            return legendToFormatterMap[legend](this.value as number);
                        },
                        style: {
                            width: 200,
                        },
                    },
                    opposite: index === 1,
                    ...getTickPositionerConfig(isSinglePoint, index),
                })),
                xAxis: {
                    tickPositioner: getXAxisTickPositioner,
                    showFirstLabel: true,
                    labels: {
                        rotation: breakdownData.length > MAX_DATA_POINTS_FOR_ROTATION ? 22.5 : undefined,
                    },
                },
                tooltip: {
                    shared: true,
                    useHTML: true,
                    outside: false,
                    positioner: tooltipPositioner,
                    formatter() {
                        if (!this.points?.length) {
                            return "";
                        }
                        const currentPointIndex = this.points[0].series.data.findIndex((point) => point.x === this.x);

                        const impressionsChangePercentage = breakdownData[currentPointIndex][1]?.impressions?.change;
                        const spendChangePercentage = breakdownData[currentPointIndex][1]?.spend?.change;
                        const thirdMetricChangePercentage = breakdownData[currentPointIndex][1]?.[thirdMetric]?.change;

                        const data = this.points
                            .map((point) => {
                                const dataType = point.series.userOptions.id as EDataType;

                                const dataTypeConfig = {
                                    [EDataType.SPEND]: {
                                        translationKey: TRANSLATION_KEYS.SPEND,
                                        formatValue: (value: number) => spendFilter(value, abbrNumberFilter()),
                                        changePercentage: spendChangePercentage,
                                    },
                                    [EDataType.IMPRESSIONS]: {
                                        translationKey: TRANSLATION_KEYS.IMPRESSIONS,
                                        formatValue: (value: number) => abbrNumberFilter()(value),
                                        changePercentage: impressionsChangePercentage,
                                    },
                                    [thirdMetric]: {
                                        translationKey: TRANSLATION_KEYS[thirdMetricToTranslationKeyMap[thirdMetric]],
                                        formatValue: (value: number) => abbrNumberFilter()(value),
                                        changePercentage: thirdMetricChangePercentage,
                                    },
                                };

                                const config = dataTypeConfig[dataType];
                                if (!config) {
                                    return null;
                                }

                                return {
                                    label: {
                                        value: translate(config.translationKey),
                                        color: point.color,
                                    },
                                    count: {
                                        value: config.formatValue(point.y),
                                    },
                                    change: {
                                        value: config.changePercentage
                                            ? minimumValueChangeFilter()(config.changePercentage)
                                            : NA_VALUE,
                                        isDecrease: config.changePercentage ? config.changePercentage < 0 : undefined,
                                        unsigned: !config.changePercentage,
                                    },
                                };
                            })
                            .filter(Boolean);

                        const tooltipTitleElements = getTooltipHeaderElement(
                            granularity,
                            this.x,
                            to.toDate(),
                            false,
                            undefined,
                            true,
                        );

                        return renderToString(
                            <ChartTooltip
                                withoutColumnTitles
                                width={368}
                                title={<TooltipTitle>{tooltipTitleElements}</TooltipTitle>}
                                columnDefinition={tooltipColumnDefinition}
                                // @ts-expect-error - item in data cannot be null
                                data={data}
                            />,
                        );
                    },
                },
            },
        ],
    );
};
